/**
 * Rules Apply Command
 *
 * Apply rule presets to project with multi-IDE support
 * Supports: Cursor, Windsurf, Antigravity, Claude Code, Codex, AGENTS.md, Gemini CLI
 */

import { Command } from 'commander';
import { promises as fs } from 'fs';
import { join } from 'path';
import { search, confirm, input, checkbox } from '@inquirer/prompts';
import { ConfigService } from '../../services/config.service.js';
import { ProjectConfigService, type RulesConfig } from '../../services/project-config.service.js';
import { RulesGeneratorService } from '../../services/rules-generator.service.js';
import { BackupService } from '../../services/backup.service.js';
import { IdeDetectionService } from '../../services/ide-detection.service.js';
import { ValidationError } from '../../errors/index.js';
import { IDE_FORMATS } from '../../config/ide-formats.js';
import { selectTheme, checkboxTheme } from '../../utils/prompt-theme.js';
import { MigrateIdeService } from '../../services/migrate-ide.service.js';
import { IDE_MIGRATION_CONFIGS, getMigrationIDEs } from '../../constants/ide-migration-configs.js';
import type { MigrateIDE, MigrateContentType } from '../../types/migrate-ide.types.js';

interface Preset {
    slug: string;
    name: string;
    description: string;
    version: string;
    tags: string[];
    stack: {
        frontend?: string | null;
        backend?: string | null;
        css?: string | null;
        database?: string | null;
        language?: string | null;
    };
    downloads: number;
}

interface PresetBundle {
    preset: {
        slug: string;
        name: string;
        description: string;
        version: string;
        tags: string[];
        stack: Record<string, string | null>;
        files: {
            required: string[];
            optional: string[];
            template: string[];
        };
        output?: {
            agentsMd?: boolean;
            ideFolder?: string;
        };
    };
    files: Record<string, string>;
}

enum ApplyStep {
    SELECT_PRESET = 'select_preset',
    SELECT_IDES = 'select_ides',
    BACKUP = 'backup',
    CONFIRM = 'confirm',
    APPLY = 'apply',
}

export function createRulesApplyCommand(): Command {
    return new Command('apply')
        .description('Apply rule preset to project with multi-IDE support')
        .argument('[preset]', 'Preset slug to apply (optional)')
        .option('--ides <ides>', 'Comma-separated list of IDE formats (cursor,windsurf,antigravity,claude,codex,agentsmd,gemini) or "all"')
        .option('--skip-backup', 'Skip backup creation')
        .option('-y, --yes', 'Skip all confirmations (auto mode)')
        .action(async (presetSlug: string | undefined, options) => {
            const configService = new ConfigService();
            const config = await configService.load();

            if (!config) {
                throw new ValidationError('Not initialized. Run "jai1 auth" first.');
            }

            const generatorService = new RulesGeneratorService();
            const backupService = new BackupService();

            // Parse options
            const autoMode = options.yes === true;
            const skipBackup = options.skipBackup === true;
            let selectedIdes: string[] = [];

            if (options.ides) {
                const idesValue = options.ides as string;

                if (idesValue.toLowerCase() === 'all') {
                    // Select all available IDEs
                    selectedIdes = generatorService.getAvailableIdes();
                } else {
                    selectedIdes = idesValue
                        .split(',')
                        .map((ide) => ide.trim())
                        .filter((ide) => generatorService.isValidIde(ide));
                }

                if (selectedIdes.length === 0) {
                    throw new ValidationError('No valid IDE formats specified');
                }
            }

            // Step 1: Select Preset
            if (!presetSlug) {
                console.log('📋 Fetching available presets...\n');

                const response = await fetch(`${config.apiUrl}/api/rules/presets`, {
                    headers: {
                        'JAI1-Access-Key': config.accessKey,
                    },
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch presets: ${response.statusText}`);
                }

                const data = (await response.json()) as { presets: Preset[]; total: number };

                if (data.total === 0) {
                    console.log('No presets available.');
                    return;
                }

                presetSlug = await search({
                    message: 'Search and select a preset (type keywords to filter):',
                    source: async (input) => {
                        const keywords = (input || '').toLowerCase().split(/\s+/).filter(Boolean);
                        const filtered = keywords.length === 0
                            ? data.presets
                            : data.presets.filter(p => {
                                const searchable = [p.name, p.description, p.slug, ...p.tags]
                                    .join(' ').toLowerCase();
                                return keywords.every(kw => searchable.includes(kw));
                            });
                        return filtered.map((p) => ({
                            name: `${p.name} - ${p.description}`,
                            value: p.slug,
                            description: `v${p.version} | ${p.tags.join(', ')}`,
                        }));
                    },
                    theme: selectTheme,
                });
            }

            // Fetch preset bundle
            console.log(`\n📦 Fetching preset: ${presetSlug}...\n`);

            const presetResponse = await fetch(`${config.apiUrl}/api/rules/presets/${presetSlug}`, {
                headers: {
                    'JAI1-Access-Key': config.accessKey,
                },
            });

            if (!presetResponse.ok) {
                if (presetResponse.status === 404) {
                    throw new Error(`Preset '${presetSlug}' not found`);
                }
                throw new Error(`Failed to fetch preset: ${presetResponse.statusText}`);
            }

            const bundle = (await presetResponse.json()) as PresetBundle;

            console.log(`✓ Preset: ${bundle.preset.name} v${bundle.preset.version}`);
            console.log(`  Files: ${Object.keys(bundle.files).length}`);

            // Step 2: Select IDE formats
            if (selectedIdes.length === 0) {
                if (autoMode) {
                    throw new ValidationError(
                        'Headless mode (-y) requires --ides flag.\n' +
                        'Examples:\n' +
                        '  j rules apply <preset> --ides cursor,antigravity -y\n' +
                        '  j rules apply <preset> --ides all -y'
                    );
                } else {
                    // Get smart suggestions
                    const detectionService = new IdeDetectionService();
                    const suggestions = await detectionService.suggestIdes();
                    const detected = await detectionService.detectActiveIdes();

                    // Show suggestions if available
                    if (suggestions.length > 0) {
                        console.log('\n💡 Smart suggestions based on your project:\n');
                        suggestions.slice(0, 3).forEach(s => {
                            const priority = s.priority === 'high' ? '⭐' : s.priority === 'medium' ? '🔸' : '▫️';
                            console.log(`${priority} ${s.name} - ${s.reason}`);
                        });
                        console.log('');
                    }

                    // Build choices with smart defaults
                    const choices = [
                        {
                            name: 'Cursor (.cursor/rules/)',
                            value: 'cursor',
                            checked: suggestions.some(s => s.ideId === 'cursor') || detected.some(d => d.id === 'cursor')
                        },
                        {
                            name: 'Windsurf (.windsurf/rules/)',
                            value: 'windsurf',
                            checked: detected.some(d => d.id === 'windsurf')
                        },
                        {
                            name: 'Antigravity (.agent/rules/)',
                            value: 'antigravity',
                            checked: detected.some(d => d.id === 'antigravity')
                        },
                        {
                            name: 'Claude Code (.claude/rules/)',
                            value: 'claude',
                            checked: suggestions.some(s => s.ideId === 'claude') || detected.some(d => d.id === 'claude')
                        },
                        {
                            name: 'Codex (AGENTS.md + .agents/skills/)',
                            value: 'codex',
                            checked: suggestions.some(s => s.ideId === 'codex') || detected.some(d => d.id === 'codex')
                        },
                        {
                            name: 'AGENTS.md (single file)',
                            value: 'agentsmd',
                            checked: suggestions.some(s => s.ideId === 'agentsmd')
                        },
                        {
                            name: 'Gemini CLI (GEMINI.md)',
                            value: 'gemini',
                            checked: detected.some(d => d.id === 'gemini')
                        },
                    ];

                    selectedIdes = await checkbox({
                        message: 'Select IDE formats to generate (pre-selected are recommended):',
                        choices,
                        required: true,
                        theme: checkboxTheme,
                    });
                }
            }

            // Resolve dependencies (e.g., Gemini requires AGENTS.md)
            const resolvedIdes = generatorService.resolveIdeDependencies(selectedIdes);

            if (resolvedIdes.length > selectedIdes.length) {
                const addedIdes = resolvedIdes.filter((ide) => !selectedIdes.includes(ide));
                console.log(`\n📌 Auto-added dependencies: ${addedIdes.join(', ')}`);
            }

            // Step 3: Create Backup
            let backupPath: string | null = null;

            if (!skipBackup) {
                console.log('\n💾 Creating backup of existing rules...');

                try {
                    backupPath = await backupService.createBackup(resolvedIdes, presetSlug);

                    if (backupPath) {
                        console.log(`✓ Backup created: ${backupPath}`);
                    } else {
                        console.log('✓ No existing files to backup');
                    }
                } catch (error) {
                    console.warn('⚠️  Backup failed, but continuing...');
                }
            }

            // Step 4: Confirm
            if (!autoMode) {
                console.log('\n📋 Summary:');
                console.log(`  Preset: ${bundle.preset.name} v${bundle.preset.version}`);
                console.log(`  IDEs: ${resolvedIdes.join(', ')}`);
                console.log(`  Files: ${Object.keys(bundle.files).length} rule files`);
                if (backupPath) {
                    console.log(`  Backup: ${backupPath}`);
                }

                const proceed = await confirm({
                    message: 'Apply these rules to the current directory?',
                    default: true,
                });

                if (!proceed) {
                    console.log('Cancelled.');
                    return;
                }
            }

            // Step 5: Apply
            console.log('\n📝 Applying preset...\n');

            // Single preset per project: delete old preset before applying new one
            const rulePresetDir = join(process.cwd(), '.jai1', 'rule-preset');

            // Remove old preset if exists
            try {
                await fs.rm(rulePresetDir, { recursive: true, force: true });
            } catch {
                // Directory doesn't exist, that's fine
            }

            // Create new preset directory
            await fs.mkdir(rulePresetDir, { recursive: true });

            // Write preset.json
            await fs.writeFile(
                join(rulePresetDir, 'preset.json'),
                JSON.stringify(bundle.preset, null, 2),
                'utf-8'
            );

            // Write all rule files from bundle
            for (const [filename, content] of Object.entries(bundle.files)) {
                const filePath = join(rulePresetDir, filename);
                await fs.mkdir(join(filePath, '..'), { recursive: true });
                await fs.writeFile(filePath, content, 'utf-8');
            }

            console.log(`✓ Saved preset to .jai1/rule-preset/`);

            const allGeneratedFiles: Array<{ ide: string; path: string; description: string }> = [];

            for (const ideId of resolvedIdes) {
                try {
                    if (ideId === 'codex') {
                        continue;
                    }

                    const files = generatorService.generateForIde(bundle, ideId);

                    for (const file of files) {
                        const fullPath = join(process.cwd(), file.path);
                        await fs.mkdir(join(fullPath, '..'), { recursive: true });
                        await fs.writeFile(fullPath, file.content, 'utf-8');

                        console.log(`✓ [${ideId}] ${file.path}`);

                        allGeneratedFiles.push({
                            ide: ideId,
                            path: file.path,
                            description: file.description,
                        });
                    }
                } catch (error) {
                    console.error(`✗ Failed to generate ${ideId} files:`, error);
                }
            }

            // Create/update project config
            const projectConfigService = new ProjectConfigService();
            const rulesConfig: RulesConfig = {
                preset: bundle.preset.slug,
                version: bundle.preset.version,
                appliedAt: new Date().toISOString(),
                ides: resolvedIdes,
                customContext: '09-custom.mdc',
                backups: backupPath
                    ? [
                        {
                            path: backupPath,
                            timestamp: new Date().toISOString(),
                            ides: resolvedIdes,
                        },
                    ]
                    : [],
            };

            // Merge with existing config if it exists
            try {
                const existingRules = await projectConfigService.loadRules();

                // Preserve existing backups
                if (existingRules?.backups && existingRules.backups.length > 0) {
                    rulesConfig.backups = [
                        ...rulesConfig.backups!,
                        ...existingRules.backups.slice(0, 5), // Keep last 5 backups
                    ];
                }
            } catch {
                // No existing config, that's fine
            }

            await projectConfigService.saveRules(rulesConfig);
            console.log('\n✓ Updated .jai1/project.json');

            // Summary
            console.log('\n✅ Preset applied successfully!\n');
            console.log('📊 Summary:');
            console.log(`  Generated ${allGeneratedFiles.length} files across ${resolvedIdes.length} IDE(s)`);

            // Group files by IDE
            const filesByIde = resolvedIdes.reduce((acc, ide) => {
                acc[ide] = allGeneratedFiles.filter((f) => f.ide === ide).length;
                return acc;
            }, {} as Record<string, number>);

            for (const [ide, count] of Object.entries(filesByIde)) {
                const format = IDE_FORMATS[ide];
                console.log(`  - ${format?.name}: ${count} files`);
            }

            if (backupPath) {
                console.log(`\n💾 Backup saved to: ${backupPath}`);
                console.log('   Use "jai1 rules restore" to restore if needed');
            }

            console.log('\n📝 Next steps:');
            console.log('  1. Review generated files in your IDE');
            console.log('  2. Edit rules in .jai1/rule-preset/ (source of truth)');
            console.log('  3. Run "jai1 rules sync" to regenerate IDE outputs');
            console.log('  4. Commit the rules to git');
            console.log('  5. Use "jai1 ide status" to check IDE configuration\n');

            // Auto-trigger IDE sync: copy .jai1/ content to selected IDEs
            const migrationIdeIds = getMigrationIDEs();
            const ideSyncMap: Record<string, MigrateIDE> = {
                'cursor': 'cursor',
                'windsurf': 'windsurf',
                'antigravity': 'antigravity',
                'claude': 'claude',
                'claudecode': 'claude',
                'codex': 'codex',
                'agentsmd': 'opencode',
                'opencode': 'opencode',
            };

            const migrationIdes = resolvedIdes
                .map(ide => ideSyncMap[ide])
                .filter((ide): ide is MigrateIDE => !!ide && migrationIdeIds.includes(ide))
                .filter((ide, index, self) => self.indexOf(ide) === index); // deduplicate

            if (migrationIdes.length > 0) {
                console.log('🔄 Auto-syncing .jai1/ content to IDE(s)...\n');

                try {
                    const migrateService = new MigrateIdeService();
                    const content = await migrateService.scanJai1Content();

                    if (content.totalCount > 0) {
                        const contentTypes: MigrateContentType[] = ['rules', 'workflows', 'commands'];
                        const results = await migrateService.migrate(
                            migrationIdes,
                            contentTypes,
                            content,
                            (result) => {
                                const icon = result.status === 'created' ? '✓' :
                                    result.status === 'updated' ? '↻' :
                                        result.status === 'error' ? '✗' : '○';
                                console.log(`   ${icon} ${result.targetPath}`);
                            }
                        );

                        const created = results.filter(r => r.status === 'created').length;
                        const updated = results.filter(r => r.status === 'updated').length;
                        console.log(`\n✅ IDE sync complete! (${created} created, ${updated} updated)\n`);
                    } else {
                        console.log('   No .jai1/ content found to sync.\n');
                    }
                } catch (error) {
                    console.warn('⚠️  IDE sync failed:', error instanceof Error ? error.message : error);
                    console.log('   You can manually run "j ide sync" to retry.\n');
                }
            }
        });
}
